import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Avatar,
  AvatarGroup,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem as MuiMenuItem,
  FormControlLabel,
  FormGroup,
  Checkbox
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  Add as AddIcon, 
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as DepartmentIcon,
  Group as GroupIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Assignment as TaskIcon,
  Check as ApproveIcon,
  Close as DenyIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { 
  downloadTaskICS, 
  downloadProjectICS,
  generateOutlookTaskURL,
  generateGoogleTaskURL,
  generateOutlookProjectURL,
  generateGoogleProjectURL
} from '../utils/calendarUtils';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import webSocketService from '../services/WebSocketService';
import ProjectTimeline from './ProjectTimeline';
import { adaptTasksToTimelineEvents } from '../utils/projectTimeline';
import { getProjectStatusCardSx, getTaskPriorityRowSx, projectTimelineRowBand } from '../utils/projectAndTaskColors';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  position?: string;
}

interface Department {
  name: string;
  memberCount: number;
}

interface ProjectMember {
  id: string;
  role: string;
  contributionPercentage: number;
  joinedDate: string;
  isActive: boolean;
  user: User;
}

interface Project {
  id: string;
  title: string;
  description: string;
  status: 'active' | 'completed' | 'archived' | 'on_hold';
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  creatorId: string;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members: ProjectMember[];
}

const PROJECTS_TIMELINE_SELECTED_IDS_KEY = 'projectsTimelineSelectedIds';

function isTimelineTaskDone(task: any): boolean {
  if (task.status === 'finished' || task.status === 'completed') return true;
  if (task.independentProgress && Array.isArray(task.assignees) && task.assignees.length > 0) {
    return task.assignees.every(
      (a: any) => a.status === 'completed' || a.status === 'finished'
    );
  }
  return false;
}

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, token, hasPermission } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Projects, 1 = My Tasks
  const [viewDialogTab, setViewDialogTab] = useState(0);
  const [projectTasks, setProjectTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [allProjectsTasks, setAllProjectsTasks] = useState<Record<string, any[]>>({});
  const [myTasks, setMyTasks] = useState<any[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [selectedTaskForDeny, setSelectedTaskForDeny] = useState<any | null>(null);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [viewTaskDialogOpen, setViewTaskDialogOpen] = useState(false);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<any | null>(null);
  const [selectedTaskForView, setSelectedTaskForView] = useState<any | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    startDate: '',
    dueDate: '',
    userIds: [] as string[],
    priority: 'medium' as 'low' | 'medium' | 'high' | 'extreme' | 'ultra',
    independentProgress: false
  });
  const [taskWarningThreshold, setTaskWarningThreshold] = useState<number>(7);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [thresholdValue, setThresholdValue] = useState<string>('7');
  const [timelineProjectIds, setTimelineProjectIds] = useState<string[]>([]);
  const [timelineFilterReady, setTimelineFilterReady] = useState(() => !hasPermission('projects.edit'));

  // Available users and departments for selection
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<Department[]>([]);

  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    status: 'active' as 'active' | 'completed' | 'archived' | 'on_hold',
    startDate: '',
    endDate: '',
    budget: '',
    members: [] as User[],
    departments: [] as Department[]
  });

  const [membersForm, setMembersForm] = useState({
    members: [] as User[],
    departments: [] as Department[]
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECTS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      } else {
        console.error('Failed to fetch projects');
        setError(t('projects.failedToFetchProjects'));
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(t('projects.errorFetchingProjects'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AVAILABLE_USERS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  }, [token]);

  const fetchAvailableDepartments = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AVAILABLE_DEPARTMENTS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableDepartments(data.departments);
      }
    } catch (error) {
      console.error('Error fetching available departments:', error);
    }
  }, [token]);

  const fetchAllProjectsTasks = useCallback(async () => {
    if (!hasPermission('projects.view')) return;
    
    try {
      const response = await fetch(getApiUrl('/tasks'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const tasksByProject: Record<string, any[]> = {};
        (data.tasks || []).forEach((task: any) => {
          if (!tasksByProject[task.projectId]) {
            tasksByProject[task.projectId] = [];
          }
          tasksByProject[task.projectId].push(task);
        });
        setAllProjectsTasks(tasksByProject);
        // Don't update myTasks here - fetchMyTasks will handle filtering by assignment
      }
    } catch (error) {
      console.error('Failed to fetch all projects tasks:', error);
    }
  }, [token, hasPermission]);

  useLayoutEffect(() => {
    if (projects.length === 0) {
      setTimelineProjectIds([]);
      setTimelineFilterReady(true);
      return;
    }
    try {
      const raw = localStorage.getItem(PROJECTS_TIMELINE_SELECTED_IDS_KEY);
      if (raw !== null) {
        const parsed = JSON.parse(raw) as string[];
        const valid = parsed.filter((id) => projects.some((p) => p.id === id));
        setTimelineProjectIds(valid);
      } else {
        setTimelineProjectIds(projects.map((p) => p.id));
      }
    } catch {
      setTimelineProjectIds(projects.map((p) => p.id));
    }
    setTimelineFilterReady(true);
  }, [projects]);

  const persistTimelineProjectIds = useCallback((ids: string[]) => {
    try {
      localStorage.setItem(PROJECTS_TIMELINE_SELECTED_IDS_KEY, JSON.stringify(ids));
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const handleTimelineProjectToggle = useCallback(
    (projectId: string, checked: boolean) => {
      setTimelineProjectIds((prev) => {
        const next = checked
          ? prev.includes(projectId)
            ? prev
            : [...prev, projectId]
          : prev.filter((id) => id !== projectId);
        persistTimelineProjectIds(next);
        return next;
      });
    },
    [persistTimelineProjectIds]
  );

  const handleTimelineSelectAll = useCallback(() => {
    const all = projects.map((p) => p.id);
    setTimelineProjectIds(all);
    persistTimelineProjectIds(all);
  }, [projects, persistTimelineProjectIds]);

  const handleTimelineDeselectAll = useCallback(() => {
    setTimelineProjectIds([]);
    persistTimelineProjectIds([]);
  }, [persistTimelineProjectIds]);

  const fetchMyTasks = useCallback(async () => {
    if (!token || !user) return;
    
    try {
      setMyTasksLoading(true);
      const response = await fetch(getApiUrl('/tasks'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter to only show tasks assigned to the current user (even for admins)
        const assignedTasks = (data.tasks || []).filter((task: any) => 
          task.assignees && task.assignees.some((a: any) => a.id === user.id)
        );
        setMyTasks(assignedTasks);
      }
    } catch (error) {
      console.error('Failed to fetch my tasks:', error);
    } finally {
      setMyTasksLoading(false);
    }
  }, [token, user]);

  const loadTaskWarningThreshold = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/settings/task_warning_threshold_days'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.value) {
          const threshold = parseInt(data.value, 10);
          if (!isNaN(threshold) && threshold > 0) {
            setTaskWarningThreshold(threshold);
            setThresholdValue(data.value);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch task warning threshold:', error);
    }
  }, [token]);

  const saveTaskWarningThreshold = useCallback(async () => {
    try {
      const threshold = parseInt(thresholdValue, 10);
      if (isNaN(threshold) || threshold < 1) {
        alert(t('tasks.invalidThreshold'));
        return;
      }

      const response = await fetch(getApiUrl('/settings/task_warning_threshold_days'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: threshold.toString() })
      });

      if (response.ok) {
        setTaskWarningThreshold(threshold);
        setThresholdDialogOpen(false);
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('tasks.failedToSaveThreshold'));
      }
    } catch (error) {
      console.error('Failed to save task warning threshold:', error);
      alert(t('tasks.failedToSaveThreshold'));
    }
  }, [token, thresholdValue, t]);

  useEffect(() => {
    if (token) {
      console.log('🔄 Fetching projects data...');
      fetchProjects();
      fetchAvailableUsers();
      fetchAvailableDepartments();
      fetchAllProjectsTasks();
      // Load threshold but don't let errors block rendering
      loadTaskWarningThreshold().catch(err => {
        console.error('Failed to load task warning threshold:', err);
      });
    }
  }, [token, fetchProjects, fetchAvailableUsers, fetchAvailableDepartments, fetchAllProjectsTasks, loadTaskWarningThreshold]);

  useEffect(() => {
    if (activeTab === 1 && token) {
      fetchMyTasks();
    }
  }, [activeTab, token, fetchMyTasks]);

  // Join project rooms and listen for task updates
  useEffect(() => {
    if (!token || !user) return;

    // Helper function to join all project rooms
    const joinAllProjectRooms = () => {
      if (!webSocketService.isConnectedToServer()) {
        return;
      }

      // Join all project rooms
      projects.forEach(project => {
        webSocketService.joinProject(project.id);
      });
    };

    // Try to join immediately if already connected
    joinAllProjectRooms();

    // Listen for WebSocket connection and join rooms when connected
    const handleConnected = () => {
      joinAllProjectRooms();
    };

    webSocketService.on('connected', handleConnected);

    // Listen for task updates
    const handleTaskUpdate = (data: any) => {
      console.log('📋 Task update received:', data);
      const { task, action } = data;

      // Update myTasks state
      setMyTasks(prevTasks => {
        const index = prevTasks.findIndex(t => t.id === task.id);
        if (index !== -1) {
          const updated = [...prevTasks];
          updated[index] = task;
          return updated;
        }
        // If task not found, it might be new - add it if user is assigned
        if (task.assignees?.some((a: any) => a.id === user?.id)) {
          return [...prevTasks, task];
        }
        return prevTasks;
      });

      // Update allProjectsTasks state
      setAllProjectsTasks(prev => {
        const projectTasks = prev[task.projectId] || [];
        const index = projectTasks.findIndex((t: any) => t.id === task.id);
        if (index !== -1) {
          const updated = [...projectTasks];
          updated[index] = task;
          return {
            ...prev,
            [task.projectId]: updated
          };
        }
        // If task not found, add it
        return {
          ...prev,
          [task.projectId]: [...projectTasks, task]
        };
      });

      // Update projectTasks if viewing that project
      if (selectedProject?.id === task.projectId) {
        setProjectTasks(prevTasks => {
          const index = prevTasks.findIndex(t => t.id === task.id);
          if (index !== -1) {
            const updated = [...prevTasks];
            updated[index] = task;
            return updated;
          }
          return [...prevTasks, task];
        });
      }
    };

    webSocketService.on('task-updated', handleTaskUpdate);

    // Cleanup: remove listeners and leave rooms
    return () => {
      webSocketService.off('connected', handleConnected);
      webSocketService.off('task-updated', handleTaskUpdate);
      // Leave all project rooms
      projects.forEach(project => {
        webSocketService.leaveProject(project.id);
      });
    };
  }, [token, user, projects, selectedProject]);

  const handleCreateProject = async () => {
    if (!projectForm.title.trim()) {
      setError(t('projects.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const requestData: any = {
        title: projectForm.title,
        description: projectForm.description,
        status: projectForm.status
      };

      // Only include optional fields if they have valid values
      if (projectForm.startDate && projectForm.startDate.trim() !== '') {
        requestData.startDate = projectForm.startDate;
      }

      if (projectForm.endDate && projectForm.endDate.trim() !== '') {
        requestData.endDate = projectForm.endDate;
      }

      if (projectForm.budget && projectForm.budget.trim() !== '') {
        requestData.budget = parseFloat(projectForm.budget);
      }

      // Add members and departments
      if (projectForm.members.length > 0) {
        requestData.members = projectForm.members.map(user => ({
          userId: user.id,
          role: 'member',
          contributionPercentage: 0
        }));
      }

      if (projectForm.departments.length > 0) {
        requestData.departments = projectForm.departments.map(dept => dept.name);
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECTS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('projects.failedToCreateProject'));
      }

      await fetchProjects();
      setCreateDialogOpen(false);
      setProjectForm({
        title: '',
        description: '',
        status: 'active',
        startDate: '',
        endDate: '',
        budget: '',
        members: [],
        departments: []
      });
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error instanceof Error ? error.message : t('projects.failedToCreateProject'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProject = async () => {
    if (!selectedProject || !projectForm.title.trim()) {
      setError(t('projects.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const requestData: any = {
        title: projectForm.title,
        description: projectForm.description,
        status: projectForm.status
      };

      // Only include optional fields if they have valid values
      if (projectForm.startDate && projectForm.startDate.trim() !== '') {
        requestData.startDate = projectForm.startDate;
      }

      if (projectForm.endDate && projectForm.endDate.trim() !== '') {
        requestData.endDate = projectForm.endDate;
      }

      if (projectForm.budget && projectForm.budget.trim() !== '') {
        requestData.budget = parseFloat(projectForm.budget);
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECT_DETAILS(selectedProject.id)), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('projects.failedToUpdateProject'));
      }

      await fetchProjects();
      setEditDialogOpen(false);
      setSelectedProject(null);
      setProjectForm({
        title: '',
        description: '',
      status: 'active',
        startDate: '',
        endDate: '',
        budget: '',
        members: [],
        departments: []
      });
    } catch (error) {
      console.error('Error updating project:', error);
      setError(error instanceof Error ? error.message : t('projects.failedToUpdateProject'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMembers = async () => {
    if (!selectedProject) return;

    try {
      setSubmitting(true);
      setError(null);

      // Add new individual members
      for (const user of membersForm.members) {
        const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECT_MEMBERS(selectedProject.id)), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: user.id,
            role: 'member',
            contributionPercentage: 0
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
                  if (errorData.error !== t('projects.userAlreadyMember')) {
          throw new Error(errorData.error || t('projects.failedToAddMember'));
          }
        }
      }

      // Add new departments
      for (const dept of membersForm.departments) {
        const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECT_DEPARTMENTS(selectedProject.id)), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            department: dept.name
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('projects.failedToAddMember'));
        }
      }

      await fetchProjects();
      setMembersDialogOpen(false);
      setSelectedProject(null);
      setMembersForm({
        members: [],
        departments: []
      });
      setActiveTab(0);
    } catch (error) {
      console.error('Error updating members:', error);
      setError(error instanceof Error ? error.message : t('projects.failedToAddMember'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedProject) return;

    try {
      const member = selectedProject.members.find(m => m.id === memberId);
      if (!member) return;

              const response = await fetch(getApiUrl(`/projects/${selectedProject.id}/members/${member.user.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('projects.failedToRemoveMember'));
      }

      await fetchProjects();
      // Update the selected project with new member list
      const updatedProject = projects.find(p => p.id === selectedProject.id);
      if (updatedProject) {
        setSelectedProject(updatedProject);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      setError(error instanceof Error ? error.message : t('projects.failedToRemoveMember'));
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm(t('projects.deleteProjectConfirm'))) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.PROJECT_DETAILS(projectId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('projects.failedToDeleteProject'));
      }

      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error instanceof Error ? error.message : t('projects.failedToDeleteProject'));
    }
  };

  const openEditDialog = (project: Project) => {
    setSelectedProject(project);
    setProjectForm({
      title: project.title,
      description: project.description || '',
      status: project.status,
      startDate: project.startDate ? new Date(project.startDate).toISOString().slice(0, 16) : '',
      endDate: project.endDate ? new Date(project.endDate).toISOString().slice(0, 16) : '',
      budget: project.budget ? project.budget.toString() : '',
      members: [],
      departments: []
    });
    setEditDialogOpen(true);
  };

  const fetchProjectTasks = async (projectId: string) => {
    if (hasPermission('projects.view')) {
      try {
        setTasksLoading(true);
        const url = getApiUrl(`/tasks/project/${projectId}`);
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProjectTasks(data.tasks || []);
        }
      } catch (error) {
        console.error('Failed to fetch project tasks:', error);
      } finally {
        setTasksLoading(false);
      }
    }
  };

  const openViewDialog = async (project: Project) => {
    setSelectedProject(project);
    setViewDialogOpen(true);
    setViewDialogTab(0);
    await fetchProjectTasks(project.id);
  };

  // Calendar menu state
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<null | HTMLElement>(null);
  const [calendarMenuType, setCalendarMenuType] = useState<'task' | 'project' | null>(null);
  const [calendarMenuData, setCalendarMenuData] = useState<any>(null);

  const handleOpenCalendarMenu = (event: React.MouseEvent<HTMLElement>, type: 'task' | 'project', data: any) => {
    event.stopPropagation();
    setCalendarMenuAnchor(event.currentTarget);
    setCalendarMenuType(type);
    setCalendarMenuData(data);
  };

  const handleCloseCalendarMenu = () => {
    setCalendarMenuAnchor(null);
    setCalendarMenuType(null);
    setCalendarMenuData(null);
  };

  const handleCalendarAction = (action: 'outlook' | 'google' | 'ics') => {
    if (!calendarMenuType || !calendarMenuData) return;

    const translations = {
      taskEvent: t('calendar.taskEvent'),
      projectEvent: t('calendar.projectEvent'),
      status: t('common.status'),
      assignees: t('tasks.assignees'),
      project: t('projects.title'),
      budget: t('projects.budget'),
      creator: t('projects.createdBy')
    };

    if (calendarMenuType === 'task') {
      if (action === 'outlook') {
        const url = generateOutlookTaskURL(calendarMenuData, translations);
        if (url) window.open(url, '_blank');
      } else if (action === 'google') {
        const url = generateGoogleTaskURL(calendarMenuData, translations);
        if (url) window.open(url, '_blank');
      } else if (action === 'ics') {
        downloadTaskICS(calendarMenuData, translations);
      }
    } else if (calendarMenuType === 'project') {
      if (action === 'outlook') {
        const url = generateOutlookProjectURL(calendarMenuData, translations);
        if (url) window.open(url, '_blank');
      } else if (action === 'google') {
        const url = generateGoogleProjectURL(calendarMenuData, translations);
        if (url) window.open(url, '_blank');
      } else if (action === 'ics') {
        downloadProjectICS(calendarMenuData, translations);
      }
    }

    handleCloseCalendarMenu();
  };

  // Helper function to get task background color/style
  const getTaskBackgroundStyle = (task: any) => {
    // Priority: Red (overdue) > Green (started) > Gradient (within threshold) > Default
    if (task.isOverdue) {
      return {
        backgroundColor: '#ffebee',
        '&:hover': {
          backgroundColor: '#ffcdd2'
        }
      };
    }
    
    const effectiveStatus = getEffectiveTaskStatus(task);
    if (effectiveStatus === 'started') {
      return {
        backgroundColor: '#e8f5e9',
        '&:hover': {
          backgroundColor: '#c8e6c9'
        }
      };
    }
    
    if (task.isNearDueDate) {
      return {
        background: 'linear-gradient(135deg, #e8f5e9 0%, #fff9c4 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #c8e6c9 0%, #fffacd 100%)'
        }
      };
    }

    return getTaskPriorityRowSx(theme, task.priority);
  };

  const handleCreateTask = async () => {
    if (!selectedProject || !taskForm.title.trim()) {
      setError(t('tasks.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      const requestBody = {
        title: taskForm.title,
        description: taskForm.description || null,
        startDate: taskForm.startDate || null,
        dueDate: taskForm.dueDate || null,
        projectId: selectedProject.id,
        userIds: taskForm.userIds,
        priority: taskForm.priority || 'medium',
        independentProgress: taskForm.independentProgress || false
      };
      const response = await fetch(getApiUrl('/tasks'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        setCreateTaskDialogOpen(false);
        setTaskForm({
          title: '',
          description: '',
          startDate: '',
          dueDate: '',
          userIds: [],
          priority: 'medium',
          independentProgress: false
        });
        await fetchProjectTasks(selectedProject.id);
        await fetchAllProjectsTasks(); // Refresh tasks in project cards
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.createError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!selectedProject) return;
    if (!window.confirm(t('tasks.deleteConfirm'))) return;

    try {
      const response = await fetch(getApiUrl(`/tasks/${taskId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchProjectTasks(selectedProject.id);
        await fetchAllProjectsTasks(); // Refresh tasks in project cards
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.deleteError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.deleteError'));
    }
  };

  const openMembersDialog = (project: Project) => {
    setSelectedProject(project);
    setMembersForm({
      members: [],
      departments: []
    });
    setMembersDialogOpen(true);
    setActiveTab(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'archived': return 'default';
      case 'on_hold': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('projects.notSet');
    return format(new Date(dateString), 'yyyy-MM-dd');
  };

  const formatBudget = (budget: number | null) => {
    if (budget === null || budget === undefined) return t('projects.notSet');
    return budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleStartTask = async (taskId: string) => {
    try {
      const response = await fetch(getApiUrl(`/tasks/${taskId}/start`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        await fetchMyTasks();
        await fetchAllProjectsTasks();
        if (selectedTaskForView?.id === taskId && data.task) {
          // Refresh the task in view dialog
          setSelectedTaskForView(data.task);
        }
        if (selectedProject) {
          await fetchProjectTasks(selectedProject.id);
        }
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.startError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.startError'));
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const response = await fetch(getApiUrl(`/tasks/${taskId}/complete`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        await fetchMyTasks();
        await fetchAllProjectsTasks();
        if (selectedTaskForView?.id === taskId && data.task) {
          // Refresh the task in view dialog
          setSelectedTaskForView(data.task);
        }
        if (selectedProject) {
          await fetchProjectTasks(selectedProject.id);
        }
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.completeError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.completeError'));
    }
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      const response = await fetch(getApiUrl(`/tasks/${taskId}/approve`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchMyTasks();
        await fetchAllProjectsTasks();
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.approveError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.approveError'));
    }
  };

  const handleOpenDenyDialog = (task: any) => {
    setSelectedTaskForDeny(task);
    setDenialReason('');
    setDenyDialogOpen(true);
  };

  const handleOpenEditTaskDialog = (task: any) => {
    setSelectedTaskForEdit(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      startDate: task.startDate ? task.startDate.split('T')[0] : '',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      userIds: task.assignees ? task.assignees.map((a: any) => a.id) : [],
      priority: task.priority || 'medium',
      independentProgress: task.independentProgress || false
    });
    setEditTaskDialogOpen(true);
  };

  const handleOpenViewTaskDialog = (task: any) => {
    // Blur the active element to prevent focus retention issues
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSelectedTaskForView(task);
    setViewTaskDialogOpen(true);
  };

  const handleUpdateTask = async () => {
    if (!selectedTaskForEdit || !taskForm.title.trim()) {
      setError(t('tasks.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(getApiUrl(`/tasks/${selectedTaskForEdit.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: taskForm.title,
          description: taskForm.description || null,
          startDate: taskForm.startDate || null,
          dueDate: taskForm.dueDate || null,
          userIds: taskForm.userIds,
          priority: taskForm.priority || 'medium',
          independentProgress: taskForm.independentProgress || false
        })
      });

      if (response.ok) {
        setEditTaskDialogOpen(false);
        setSelectedTaskForEdit(null);
        setTaskForm({
          title: '',
          description: '',
          startDate: '',
          dueDate: '',
          userIds: [],
          priority: 'medium',
          independentProgress: false
        });
        if (selectedProject) {
          await fetchProjectTasks(selectedProject.id);
        }
        await fetchAllProjectsTasks();
        await fetchMyTasks();
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.updateError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDenyTask = async () => {
    if (!selectedTaskForDeny) return;

    try {
      const response = await fetch(getApiUrl(`/tasks/${selectedTaskForDeny.id}/deny`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          denialReason: denialReason || null
        })
      });

      if (response.ok) {
        setDenyDialogOpen(false);
        setDenialReason('');
        setSelectedTaskForDeny(null);
        await fetchMyTasks();
        await fetchAllProjectsTasks();
      } else {
        const data = await response.json();
        setError(data.error || t('tasks.denyError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tasks.denyError'));
    }
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'warning';
      case 'started': return 'info';
      case 'assigned': return 'default';
      case 'finished': return 'success';
      default: return 'default';
    }
  };

  const getTaskStatusLabel = (status: string) => {
    return t(`tasks.status.${status}`);
  };

  const isTaskAssignedToUser = (task: any) => {
    if (!task.assignees || !user) return false;
    return task.assignees.some((a: any) => a.id === user.id);
  };

  // Get effective task status (user's own status if independent progress, otherwise task status)
  const getEffectiveTaskStatus = (task: any) => {
    if (task.independentProgress && user) {
      const userAssignment = task.assignees?.find((a: any) => a.id === user.id);
      return userAssignment?.status || task.status;
    }
    return task.status;
  };

  // Get user's assignment status for independent progress tasks
  const getUserAssignmentStatus = (task: any) => {
    if (!task.independentProgress || !user) return null;
    const userAssignment = task.assignees?.find((a: any) => a.id === user.id);
    return userAssignment?.status || null;
  };

  const formatTaskDate = (dateString: string | null | undefined, emptyLabel = t('tasks.noDueDate')) => {
    if (!dateString) return emptyLabel;
    return format(new Date(dateString), 'yyyy-MM-dd');
  };

  const canEditProjects = hasPermission('projects.edit');
  const canViewProjects = hasPermission('projects.view');

  const mergedTimelineEvents = useMemo(() => {
    if (!canViewProjects || projects.length === 0) return [];

    const allowed = new Set(timelineProjectIds);
    const ids = projects.map((p) => p.id).filter((pid) => allowed.has(pid));
    const titleById = new Map(projects.map((p) => [p.id, p.title] as const));
    const mergedRaw: any[] = [];

    for (const pid of ids) {
      const tasks = allProjectsTasks[pid] || [];
      for (const task of tasks) {
        if (canEditProjects || task.assignees?.some((a: { id: string }) => a.id === user?.id)) {
          mergedRaw.push({ ...task, projectId: task.projectId ?? pid });
        }
      }
    }

    const events = adaptTasksToTimelineEvents(mergedRaw);
    const showProjectInRow = ids.length > 1;
    return events.map((e) => {
      const raw = mergedRaw.find((t) => t.id === e.id);
      return {
        ...e,
        projectTitle: showProjectInRow ? titleById.get(e.projectId) : undefined,
        timelineDone: raw ? isTimelineTaskDone(raw) : false
      };
    });
  }, [canViewProjects, canEditProjects, projects, timelineProjectIds, allProjectsTasks, user?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
        <Typography variant="h4" component="h1">
          {t('projects.title')}
        </Typography>
          <Typography variant="body2" color="text.secondary">
            {projects.filter(p => p.status === 'active').length} {t('projects.activeProjects')} • {projects.length} {t('projects.totalProjects')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          {t('projects.newProject')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Projects and My Tasks */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={t('projects.title')} />
          <Tab label={t('tasks.myTasks')} icon={<TaskIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* My Tasks Panel */}
      {activeTab === 1 && (
        <Box>
          {myTasksLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tasks.title')}</TableCell>
                    <TableCell>{t('tasks.project')}</TableCell>
                    <TableCell>{t('tasks.assignees')}</TableCell>
                    <TableCell>{t('tasks.statusLabel')}</TableCell>
                    <TableCell>{t('tasks.startDate')}</TableCell>
                    <TableCell>{t('tasks.dueDate')}</TableCell>
                    <TableCell>{t('tasks.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myTasks.length === 0 ? (
                      <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('tasks.noTasks')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    myTasks.map((task) => (
                      <TableRow 
                        key={task.id}
                        sx={getTaskBackgroundStyle(task)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {task.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {task.project?.title || '-'}
                        </TableCell>
                        <TableCell>
                          {task.assignees && task.assignees.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {task.assignees.map((assignee: any) => (
                                <Chip
                                  key={assignee.id}
                                  label={`${assignee.firstName} ${assignee.lastName}`}
                                  size="small"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTaskStatusLabel(getEffectiveTaskStatus(task))}
                            color={getTaskStatusColor(getEffectiveTaskStatus(task)) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatTaskDate(task.startDate, t('tasks.noStartDate'))}</TableCell>
                        <TableCell>{formatTaskDate(task.dueDate)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {task.dueDate && (
                              <Tooltip title={t('calendar.addToCalendar')}>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => handleOpenCalendarMenu(e, 'task', task)}
                                >
                                  <EventIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={t('tasks.viewDetails')}>
                              <IconButton size="small" onClick={() => handleOpenViewTaskDialog(task)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {getEffectiveTaskStatus(task) === 'assigned' && (
                              <Tooltip title={t('tasks.start')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleStartTask(task.id)}
                                >
                                  <StartIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {getEffectiveTaskStatus(task) === 'started' && (
                              <Tooltip title={t('tasks.complete')}>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleCompleteTask(task.id)}
                                >
                                  <CompleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {hasPermission('projects.edit') && (task.independentProgress ? 
                              (task.assignees?.every((a: any) => a.status === 'completed')) : 
                              (task.status === 'completed')
                            ) && (
                              <>
                                <Tooltip title={t('tasks.approve')}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApproveTask(task.id)}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('tasks.deny')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenDenyDialog(task)}
                                  >
                                    <DenyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Projects Panel */}
      {activeTab === 0 && (
      <>
      {canViewProjects && projects.length > 0 && timelineFilterReady && (
        <Box sx={{ width: 1, maxWidth: '100%', mb: 3 }}>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor:
                timelineProjectIds.length === 1
                  ? projectTimelineRowBand(theme, timelineProjectIds[0])
                  : 'background.default'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1
              }}
            >
              <Typography variant="subtitle2" component="h2">
                {t('projects.timelineProjectsFilter')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={handleTimelineSelectAll}>
                  {t('projects.timelineSelectAll')}
                </Button>
                <Button size="small" variant="outlined" onClick={handleTimelineDeselectAll}>
                  {t('projects.timelineDeselectAll')}
                </Button>
              </Box>
            </Box>
            <FormGroup row sx={{ flexWrap: 'wrap', columnGap: 1.5, rowGap: 1 }}>
              {[...projects]
                .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
                .map((project) => (
                  <FormControlLabel
                    key={project.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={timelineProjectIds.includes(project.id)}
                        onChange={(e) => handleTimelineProjectToggle(project.id, e.target.checked)}
                      />
                    }
                    label={project.title}
                    sx={{
                      mr: 0,
                      maxWidth: 300,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: projectTimelineRowBand(theme, project.id),
                      transition: 'filter 0.15s ease',
                      '&:hover': {
                        filter: theme.palette.mode === 'dark' ? 'brightness(1.06)' : 'brightness(0.96)'
                      }
                    }}
                  />
                ))}
            </FormGroup>
            {timelineProjectIds.length === 0 && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 1 }}>
                {t('projects.timelineNoProjectsSelected')}
              </Alert>
            )}
          </Box>
          <ProjectTimeline
            tasks={mergedTimelineEvents}
            onTaskClick={(projectId, taskId) => {
              const raw = (allProjectsTasks[projectId] || []).find((t: { id: string }) => t.id === taskId);
              if (!raw) return;
              const project = projects.find((p) => p.id === projectId);
              handleOpenViewTaskDialog(
                raw.project
                  ? raw
                  : {
                      ...raw,
                      project: project ? { id: project.id, title: project.title } : undefined
                    }
              );
            }}
          />
        </Box>
      )}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 3 
      }}>
        {projects.length === 0 ? (
          <Card sx={{ gridColumn: '1 / -1' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {t('projects.noProjectsYet')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('projects.createFirstProject')}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          projects.map((project) => (
          <Card
            key={project.id}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              ...getProjectStatusCardSx(theme, project.status),
              bgcolor: projectTimelineRowBand(theme, project.id)
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  {project.title}
                </Typography>
                    {project.status === 'active' && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'success.main',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    )}
                  </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(project.startDate || project.endDate) && (
                    <Tooltip title={t('calendar.addToCalendar')}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenCalendarMenu(e, 'project', project)}
                      >
                        <EventIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Chip
                    label={project.status}
                    color={getStatusColor(project.status) as any}
                    size="small"
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.description || t('projects.noDescriptionProvided')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('projects.budget')}:</strong> {formatBudget(project.budget)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('projects.startDate')}:</strong> {formatDate(project.startDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('projects.endDate')}:</strong> {formatDate(project.endDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('projects.createdBy')}:</strong> {project.creator.firstName} {project.creator.lastName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('projects.members')}:</strong>
                    </Typography>
                    <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                      {project.members?.slice(0, 3).map((member) => (
                        <Avatar key={member.id} sx={{ width: 24, height: 24 }}>
                          {member.user.firstName.charAt(0)}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    {project.members && project.members.length > 3 && (
                <Typography variant="body2" color="text.secondary">
                  +{project.members.length - 3} {t('projects.moreMembers')}
                </Typography>
                    )}
                  </Box>
                  {((allProjectsTasks[project.id]?.length ?? 0) > 0 ||
                    (hasPermission('projects.edit') && project.status === 'active')) && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 0.5
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" component="div">
                          <strong>{t('projects.tasks')}:</strong>
                        </Typography>
                        {hasPermission('projects.edit') && project.status === 'active' && (
                          <Tooltip title={t('tasks.createTask')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedProject(project);
                                setTaskForm({
                                  title: '',
                                  description: '',
                                  startDate: format(new Date(), 'yyyy-MM-dd'),
                                  dueDate: '',
                                  userIds: [],
                                  priority: 'medium',
                                  independentProgress: false
                                });
                                setCreateTaskDialogOpen(true);
                              }}
                              aria-label={t('tasks.createTask')}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {(allProjectsTasks[project.id] ?? [])
                        .filter((task: any) => 
                          !hasPermission('projects.edit') 
                            ? task.assignees?.some((a: any) => a.id === user?.id)
                            : true
                        )
                        .slice(0, 3)
                        .map((task: any) => (
                          <Box 
                            key={task.id} 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 0.5, 
                              mb: 1,
                              cursor: 'pointer',
                              borderRadius: 1,
                              px: 0.5,
                              py: 0.25,
                              ...getTaskBackgroundStyle(task)
                            }}
                            onClick={() => handleOpenViewTaskDialog(task)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Typography variant="caption" component="span">
                                  {task.title}
                                </Typography>
                                <Chip
                                  label={t(`tasks.status.${getEffectiveTaskStatus(task)}`)}
                                  color={
                                    getEffectiveTaskStatus(task) === 'completed' ? 'warning' :
                                    getEffectiveTaskStatus(task) === 'started' ? 'info' :
                                    getEffectiveTaskStatus(task) === 'finished' ? 'success' : 'default'
                                  }
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {task.dueDate && (
                                  <Tooltip title={t('calendar.addToCalendar')}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleOpenCalendarMenu(e, 'task', task)}
                                    >
                                      <EventIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasPermission('projects.edit') && (
                                  <Tooltip title={t('tasks.editTask')}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenEditTaskDialog(task);
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {getEffectiveTaskStatus(task) === 'assigned' && isTaskAssignedToUser(task) && (
                                  <Tooltip title={t('tasks.start')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartTask(task.id);
                                      }}
                                    >
                                      <StartIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {getEffectiveTaskStatus(task) === 'started' && isTaskAssignedToUser(task) && (
                                  <Tooltip title={t('tasks.complete')}>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCompleteTask(task.id);
                                      }}
                                    >
                                      <CompleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasPermission('projects.edit') && (task.independentProgress ? 
                                  (task.assignees?.every((a: any) => a.status === 'completed')) : 
                                  (task.status === 'completed')
                                ) && (
                                  <>
                                    <Tooltip title={t('tasks.approve')}>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveTask(task.id);
                                        }}
                                      >
                                        <ApproveIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('tasks.deny')}>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDenyDialog(task);
                                        }}
                                      >
                                        <DenyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </Box>
                            {(hasPermission('projects.edit') || isTaskAssignedToUser(task)) && task.assignees && task.assignees.length > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {t('tasks.assignees')}:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {task.assignees.map((assignee: any) => {
                                    const assigneeStatus = task.independentProgress ? assignee.status : undefined;
                                    const statusColor = assigneeStatus === 'started' ? 'info' : 
                                                      assigneeStatus === 'completed' ? 'warning' : 
                                                      assigneeStatus === 'finished' ? 'success' : 'default';
                                    return (
                                      <Chip
                                        key={assignee.id}
                                        label={task.independentProgress && assigneeStatus ? 
                                          `${assignee.firstName} ${assignee.lastName} [${t(`tasks.status.${assigneeStatus}`)}]` :
                                          `${assignee.firstName} ${assignee.lastName}`
                                        }
                                        size="small"
                                        color={task.independentProgress ? statusColor as any : 'default'}
                                        sx={{ fontSize: '0.6rem', height: '18px' }}
                                      />
                                    );
                                  })}
                                </Box>
                                {task.independentProgress && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5 }}>
                                    ({task.assignees.filter((a: any) => a.status === 'started').length}/{task.assignees.length} {t('tasks.status.started')}, {task.assignees.filter((a: any) => a.status === 'completed').length}/{task.assignees.length} {t('tasks.status.completed')})
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        ))}
                      {(allProjectsTasks[project.id] ?? []).filter((task: any) => 
                        !hasPermission('projects.edit') 
                          ? task.assignees?.some((a: any) => a.id === user?.id)
                          : true
                      ).length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{(allProjectsTasks[project.id] ?? []).filter((task: any) => 
                            !hasPermission('projects.edit') 
                              ? task.assignees?.some((a: any) => a.id === user?.id)
                              : true
                          ).length - 3} {t('tasks.more')}
                        </Typography>
                      )}
                    </Box>
                  )}
              </Box>
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<ViewIcon />} onClick={() => openViewDialog(project)}>
                {t('projects.viewDetails')}
              </Button>
                {(project.creatorId === user?.id || hasPermission('projects.edit')) && (
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {project.status === 'active' && (
                      <Tooltip title={t('projects.manageMembers')}>
                        <IconButton
                          size="small"
                          onClick={() => openMembersDialog(project)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText'
                            }
                          }}
                        >
                          <GroupIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                                          <Tooltip title={t('projects.editProjectTooltip')}>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(project)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                                          <Tooltip title={t('projects.deleteProjectTooltip')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
            </CardActions>
          </Card>
          ))
        )}
      </Box>
      </>
      )}

      {/* Create Project Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('projects.createProject')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t('projects.projectTitle')}
              value={projectForm.title}
              onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('projects.description')}
              multiline
              rows={4}
              value={projectForm.description}
              onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('projects.status')}</InputLabel>
                <Select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value as any })}
                  label={t('projects.status')}
                >
                  <MenuItem value="active">{t('projects.statuses.active')}</MenuItem>
                  <MenuItem value="completed">{t('projects.statuses.completed')}</MenuItem>
                  <MenuItem value="archived">{t('projects.statuses.archived')}</MenuItem>
                  <MenuItem value="on_hold">{t('projects.statuses.on_hold')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('projects.budgetOptional')}
                type="number"
                value={projectForm.budget}
                onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('projects.startDateOptional')}
                type="datetime-local"
                value={projectForm.startDate}
                onChange={(e) => setProjectForm({ ...projectForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={t('projects.endDateOptional')}
                type="datetime-local"
                value={projectForm.endDate}
                onChange={(e) => setProjectForm({ ...projectForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            {/* Member Selection */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Add Members</Typography>
              <Autocomplete
                multiple
                options={availableUsers}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={projectForm.members}
                onChange={(event, newValue) => setProjectForm({ ...projectForm, members: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Individual Members"
                    placeholder="Search users..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            {/* Department Selection */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Add Departments</Typography>
              <Autocomplete
                multiple
                options={availableDepartments}
                getOptionLabel={(option) => `${option.name} (${option.memberCount} members)`}
                value={projectForm.departments}
                onChange={(event, newValue) => setProjectForm({ ...projectForm, departments: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Departments"
                    placeholder="Search departments..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DepartmentIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.memberCount} members
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            {/* Selected Members Summary */}
            {(projectForm.members.length > 0 || projectForm.departments.length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Project Members Summary:
                </Typography>
                <List dense>
                  {projectForm.members.map((member) => (
                    <ListItem key={member.id} sx={{ py: 0 }}>
                      <ListItemText
                        primary={`${member.firstName} ${member.lastName}`}
                        secondary={member.email}
                      />
                      <Chip label="Individual" size="small" color="primary" />
                    </ListItem>
                  ))}
                  {projectForm.departments.map((dept) => (
                    <ListItem key={dept.name} sx={{ py: 0 }}>
                      <ListItemText
                        primary={dept.name}
                        secondary={`${dept.memberCount} members`}
                      />
                      <Chip label="Department" size="small" color="secondary" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained" 
            disabled={submitting || !projectForm.title}
          >
            {submitting ? <CircularProgress size={20} /> : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('projects.editProject')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t('projects.title')}
              value={projectForm.title}
              onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('projects.description')}
              multiline
              rows={4}
              value={projectForm.description}
              onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('projects.status')}</InputLabel>
                <Select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value as any })}
                  label={t('projects.status')}
                >
                  <MenuItem value="active">{t('projects.statuses.active')}</MenuItem>
                  <MenuItem value="completed">{t('projects.statuses.completed')}</MenuItem>
                  <MenuItem value="archived">{t('projects.statuses.archived')}</MenuItem>
                  <MenuItem value="on_hold">{t('projects.statuses.on_hold')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('projects.budgetOptional')}
                type="number"
                value={projectForm.budget}
                onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('projects.startDateOptional')}
                type="datetime-local"
                value={projectForm.startDate}
                onChange={(e) => setProjectForm({ ...projectForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={t('projects.endDateOptional')}
                type="datetime-local"
                value={projectForm.endDate}
                onChange={(e) => setProjectForm({ ...projectForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleEditProject} 
            variant="contained" 
            disabled={submitting || !projectForm.title}
          >
            {submitting ? <CircularProgress size={20} /> : t('projects.updateProject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Project Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProject?.title || 'Project Details'}</DialogTitle>
        <DialogContent>
          {selectedProject ? (
            <Box sx={{ mt: 1 }}>
              <Tabs value={viewDialogTab} onChange={(e, newValue) => setViewDialogTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={t('projects.details')} />
                <Tab label={t('projects.tasks')} />
              </Tabs>
              
              {viewDialogTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ flex: 1 }}>{selectedProject.description || t('projects.noDescriptionProvided')}</Typography>
                {(selectedProject.startDate || selectedProject.endDate) && (
                  <Tooltip title={t('calendar.addToCalendar')}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenCalendarMenu(e, 'project', selectedProject)}
                    >
                      <EventIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Typography variant="body2"><strong>Status:</strong> {selectedProject.status}</Typography>
                <Typography variant="body2"><strong>{t('projects.budget')}:</strong> {formatBudget(selectedProject.budget)}</Typography>
                <Typography variant="body2"><strong>{t('projects.startDate')}:</strong> {formatDate(selectedProject.startDate)}</Typography>
                <Typography variant="body2"><strong>{t('projects.endDate')}:</strong> {formatDate(selectedProject.endDate)}</Typography>
                <Typography variant="body2" sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <strong>{t('projects.createdBy')}:</strong> {selectedProject.creator.firstName} {selectedProject.creator.lastName}
                </Typography>
              </Box>
              <Divider />
              <Typography variant="h6">{t('projects.members')}</Typography>
              {selectedProject.members?.length ? (
                <List>
                  {selectedProject.members.map((member) => (
                    <ListItem key={member.id}>
                      <ListItemText
                        primary={`${member.user.firstName} ${member.user.lastName}`}
                        secondary={`${member.user.email} • ${member.role}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No members added yet.</Typography>
                  )}
                </Box>
              )}
              
              {viewDialogTab === 1 && (
                <Box>
                  {hasPermission('projects.edit') && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {t('tasks.warningThreshold')}: {taskWarningThreshold} {t('tasks.warningThresholdDays')}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setThresholdValue(taskWarningThreshold.toString());
                              setThresholdDialogOpen(true);
                            }}
                          >
                            {t('tasks.editThreshold')}
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('tasks.thresholdDescription', { count: taskWarningThreshold })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setTaskForm({
                              title: '',
                              description: '',
                              startDate: format(new Date(), 'yyyy-MM-dd'),
                              dueDate: '',
                              userIds: [],
                              priority: 'medium',
                              independentProgress: false
                            });
                            setCreateTaskDialogOpen(true);
                          }}
                        >
                          {t('tasks.createTask')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {tasksLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : projectTasks.length > 0 ? (
                    <List>
                      {projectTasks.map((task) => (
                        <ListItem 
                          key={task.id} 
                          sx={{ 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1, 
                            mb: 1,
                            cursor: 'pointer',
                            ...getTaskBackgroundStyle(task)
                          }}
                          onClick={() => handleOpenViewTaskDialog(task)}
                        >
                          <ListItemText
                            primary={task.title}
                            secondary={
                              <>
                                <Typography component="span" variant="caption">
                                  {t(`tasks.status.${getEffectiveTaskStatus(task)}`)} •{' '}
                                  {task.startDate ? formatDate(task.startDate) : t('tasks.noStartDate')} →{' '}
                                  {task.dueDate ? formatDate(task.dueDate) : t('tasks.noDueDate')}
                                  {task.independentProgress && hasPermission('projects.edit') && (
                                    <> • {t('tasks.independentProgress')}</>
                                  )}
                                </Typography>
                                {task.assignees && task.assignees.length > 0 && (
                                  <>
                                    <br />
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      {t('tasks.assignees')}: {task.assignees.map((a: any) => `${a.firstName} ${a.lastName}`).join(', ')}
                                    </Typography>
                                  </>
                                )}
                              </>
                            }
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={t(`tasks.status.${task.status}`)}
                              color={
                                task.status === 'completed' ? 'warning' :
                                task.status === 'started' ? 'info' :
                                task.status === 'finished' ? 'success' : 'default'
                              }
                              size="small"
                            />
                            {task.dueDate && (
                              <Tooltip title={t('calendar.addToCalendar')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenCalendarMenu(e, 'task', task)}
                                >
                                  <EventIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission('projects.edit') && (
                              <>
                                <Tooltip title={t('tasks.editTask')}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenEditTaskDialog(task);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('tasks.delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTask(task.id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('tasks.noTasks')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No project selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={createTaskDialogOpen} onClose={() => setCreateTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('tasks.createTask')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('tasks.title')}
            value={taskForm.title}
            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('tasks.description')}
            value={taskForm.description}
            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label={t('tasks.startDateOptional')}
            type="date"
            value={taskForm.startDate}
            onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label={t('tasks.dueDate')}
            type="date"
            value={taskForm.dueDate}
            onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('workPlans.priority')}</InputLabel>
            <Select
              value={taskForm.priority}
              onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as 'low' | 'medium' | 'high' | 'extreme' | 'ultra' })}
              label={t('workPlans.priority')}
            >
              <MenuItem value="low">{t('workPlans.priorities.low')}</MenuItem>
              <MenuItem value="medium">{t('workPlans.priorities.medium')}</MenuItem>
              <MenuItem value="high">{t('workPlans.priorities.high')}</MenuItem>
              <MenuItem value="extreme">{t('workPlans.priorities.extreme')}</MenuItem>
              <MenuItem value="ultra">{t('workPlans.priorities.ultra')}</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={(() => {
              const members = selectedProject?.members || [];
              const uniqueUsers = new Map<string, User>();
              members.forEach(m => {
                if (m.user && !uniqueUsers.has(m.user.id)) {
                  uniqueUsers.set(m.user.id, m.user);
                }
              });
              return Array.from(uniqueUsers.values());
            })()}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={(() => {
              const members = selectedProject?.members || [];
              const uniqueUsers = new Map<string, User>();
              members.forEach(m => {
                if (m.user && !uniqueUsers.has(m.user.id)) {
                  uniqueUsers.set(m.user.id, m.user);
                }
              });
              return Array.from(uniqueUsers.values()).filter(u => taskForm.userIds.includes(u.id));
            })()}
            onChange={(e, newValue) => {
              setTaskForm({ ...taskForm, userIds: newValue.map(u => u.id) });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('tasks.assignees')}
                margin="normal"
                placeholder={selectedProject?.members?.length ? '' : t('tasks.noProjectMembers')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={`${option.firstName} ${option.lastName}`}
                />
              ))
            }
            disabled={!selectedProject?.members?.length}
          />
          {hasPermission('projects.edit') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskForm.independentProgress}
                  onChange={(e) => setTaskForm({ ...taskForm, independentProgress: e.target.checked })}
                />
              }
              label={t('tasks.independentProgress')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTaskDialogOpen(false)}>{t('tasks.cancel')}</Button>
          <Button 
            onClick={handleCreateTask} 
            variant="contained"
            disabled={submitting || !taskForm.title.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : t('tasks.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog open={membersDialogOpen} onClose={() => setMembersDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              Manage Project Members - {selectedProject?.title}
            </Typography>
            {selectedProject?.status === 'active' && (
              <Chip 
                label="Active Project" 
                color="success" 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          {selectedProject?.status !== 'active' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Note: Member management is only available for active projects
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="Current Members" />
              <Tab label="Add Members" />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Current Members ({selectedProject?.members?.length || 0})
                </Typography>
                {selectedProject?.members && selectedProject.members.length > 0 ? (
                  <List>
                    {selectedProject.members.map((member) => (
                      <ListItem key={member.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={`${member.user.firstName} ${member.user.lastName}`}
                          secondary={`${member.user.email} • Role: ${member.role}`}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={member.role} 
                            size="small" 
                            color={member.role === 'creator' ? 'primary' : 'default'}
                          />
                          {member.role !== 'creator' && (
                            <Tooltip title="Remove Member">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No members added yet.
                  </Typography>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add New Members
                </Typography>
                
                {/* Add Individual Members */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Add Individual Members</Typography>
                  <Autocomplete
                    multiple
                    options={availableUsers.filter(user => 
                      !selectedProject?.members?.some(member => member.user.id === user.id)
                    )}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                    value={membersForm.members}
                    onChange={(event, newValue) => setMembersForm({ ...membersForm, members: newValue })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Individual Members"
                        placeholder="Search users..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  />
                </Box>

                {/* Add Departments */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Add Departments</Typography>
                  <Autocomplete
                    multiple
                    options={availableDepartments}
                    getOptionLabel={(option) => `${option.name} (${option.memberCount} members)`}
                    value={membersForm.departments}
                    onChange={(event, newValue) => setMembersForm({ ...membersForm, departments: newValue })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Departments"
                        placeholder="Search departments..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DepartmentIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.memberCount} members
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  />
                </Box>

                {/* Summary */}
                {(membersForm.members.length > 0 || membersForm.departments.length > 0) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Will be added:
                    </Typography>
                    <List dense>
                      {membersForm.members.map((member) => (
                        <ListItem key={member.id} sx={{ py: 0 }}>
                          <ListItemText
                            primary={`${member.firstName} ${member.lastName}`}
                            secondary={member.email}
                          />
                          <Chip label="Individual" size="small" color="primary" />
                        </ListItem>
                      ))}
                      {membersForm.departments.map((dept) => (
                        <ListItem key={dept.name} sx={{ py: 0 }}>
                          <ListItemText
                            primary={dept.name}
                            secondary={`${dept.memberCount} members`}
                          />
                          <Chip label="Department" size="small" color="secondary" />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Cancel</Button>
          {activeTab === 1 && (
            <Button 
              onClick={handleUpdateMembers} 
              variant="contained" 
              disabled={submitting || (membersForm.members.length === 0 && membersForm.departments.length === 0)}
            >
              {submitting ? <CircularProgress size={20} /> : 'Add Members'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Deny Task Dialog */}
      <Dialog open={denyDialogOpen} onClose={() => setDenyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks.denyTask')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('tasks.denialReason')}
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyDialogOpen(false)}>{t('tasks.cancel')}</Button>
          <Button onClick={handleDenyTask} variant="contained" color="error">
            {t('tasks.deny')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={editTaskDialogOpen} onClose={() => setEditTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('tasks.editTask')}</DialogTitle>
        <DialogContent>
          {selectedTaskForEdit && (
            <>
              <TextField
                fullWidth
                label={t('tasks.project')}
                value={selectedTaskForEdit.project?.title || '-'}
                margin="normal"
                disabled
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={t('tasks.title')}
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('tasks.description')}
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label={t('tasks.startDate')}
                type="date"
                value={taskForm.startDate}
                onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={t('tasks.dueDate')}
                type="date"
                value={taskForm.dueDate}
                onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('workPlans.priority')}</InputLabel>
                <Select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as 'low' | 'medium' | 'high' | 'extreme' | 'ultra' })}
                  label={t('workPlans.priority')}
                >
                  <MenuItem value="low">{t('workPlans.priorities.low')}</MenuItem>
                  <MenuItem value="medium">{t('workPlans.priorities.medium')}</MenuItem>
                  <MenuItem value="high">{t('workPlans.priorities.high')}</MenuItem>
                  <MenuItem value="extreme">{t('workPlans.priorities.extreme')}</MenuItem>
                  <MenuItem value="ultra">{t('workPlans.priorities.ultra')}</MenuItem>
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                options={(() => {
                  const members = selectedProject?.members || [];
                  const uniqueUsers = new Map<string, User>();
                  members.forEach(m => {
                    if (m.user && !uniqueUsers.has(m.user.id)) {
                      uniqueUsers.set(m.user.id, m.user);
                    }
                  });
                  return Array.from(uniqueUsers.values());
                })()}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={(() => {
                  const members = selectedProject?.members || [];
                  const uniqueUsers = new Map<string, User>();
                  members.forEach(m => {
                    if (m.user && !uniqueUsers.has(m.user.id)) {
                      uniqueUsers.set(m.user.id, m.user);
                    }
                  });
                  return Array.from(uniqueUsers.values()).filter(u => taskForm.userIds.includes(u.id));
                })()}
                onChange={(e, newValue) => {
                  setTaskForm({ ...taskForm, userIds: newValue.map(u => u.id) });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('tasks.assignees')}
                    margin="normal"
                    placeholder={selectedProject?.members?.length ? '' : t('tasks.noProjectMembers')}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={`${option.firstName} ${option.lastName}`}
                    />
                  ))
                }
                disabled={!selectedProject?.members?.length}
              />
              {hasPermission('projects.edit') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={taskForm.independentProgress}
                      onChange={(e) => setTaskForm({ ...taskForm, independentProgress: e.target.checked })}
                    />
                  }
                  label={t('tasks.independentProgress')}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTaskDialogOpen(false)}>{t('tasks.cancel')}</Button>
          <Button 
            onClick={handleUpdateTask} 
            variant="contained"
            disabled={submitting || !taskForm.title.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : t('tasks.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={viewTaskDialogOpen} onClose={() => setViewTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTaskForView?.title || t('tasks.viewDetails')}</DialogTitle>
        <DialogContent>
          {selectedTaskForView && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('tasks.project')}:</strong> {selectedTaskForView.project?.title || '-'}
              </Typography>
              {selectedTaskForView.independentProgress && hasPermission('projects.edit') ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                      <strong>{t('tasks.independentProgress')}:</strong>
                    </Typography>
                    <Chip label={t('common.yes')} size="small" color="info" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t('tasks.startDate')}:</strong>{' '}
                    {selectedTaskForView.startDate ? formatDate(selectedTaskForView.startDate) : t('tasks.noStartDate')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t('tasks.dueDate')}:</strong> {selectedTaskForView.dueDate ? formatDate(selectedTaskForView.dueDate) : t('tasks.noDueDate')}
                  </Typography>
                  {selectedTaskForView.description && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        <strong>{t('tasks.description')}:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedTaskForView.description}
                      </Typography>
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('tasks.assignees')}:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {selectedTaskForView.assignees && selectedTaskForView.assignees.length > 0 ? (
                      selectedTaskForView.assignees.map((assignee: any) => {
                        const assigneeStatus = assignee.status || 'assigned';
                        const statusColor = assigneeStatus === 'started' ? 'info' : 
                                          assigneeStatus === 'completed' ? 'warning' : 
                                          assigneeStatus === 'finished' ? 'success' : 'default';
                        return (
                          <Box key={assignee.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {assignee.firstName} {assignee.lastName}:
                            </Typography>
                            <Chip
                              label={t(`tasks.status.${assigneeStatus}`)}
                              size="small"
                              color={statusColor as any}
                            />
                          </Box>
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('tasks.noAssignees')}
                      </Typography>
                    )}
                    {selectedTaskForView.assignees && selectedTaskForView.assignees.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {t('tasks.progressSummary', {
                          started: selectedTaskForView.assignees.filter((a: any) => a.status === 'started').length,
                          completed: selectedTaskForView.assignees.filter((a: any) => a.status === 'completed').length,
                          total: selectedTaskForView.assignees.length
                        })}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                      <strong>{t('tasks.statusLabel')}:</strong>
                    </Typography>
                    <Chip
                      label={getTaskStatusLabel(selectedTaskForView.independentProgress && user ? 
                        (selectedTaskForView.assignees?.find((a: any) => a.id === user.id)?.status || selectedTaskForView.status) :
                        selectedTaskForView.status
                      )}
                      color={
                        (selectedTaskForView.independentProgress && user ? 
                          (selectedTaskForView.assignees?.find((a: any) => a.id === user.id)?.status || selectedTaskForView.status) :
                          selectedTaskForView.status
                        ) === 'completed' ? 'warning' :
                        (selectedTaskForView.independentProgress && user ? 
                          (selectedTaskForView.assignees?.find((a: any) => a.id === user.id)?.status || selectedTaskForView.status) :
                          selectedTaskForView.status
                        ) === 'started' ? 'info' :
                        (selectedTaskForView.independentProgress && user ? 
                          (selectedTaskForView.assignees?.find((a: any) => a.id === user.id)?.status || selectedTaskForView.status) :
                          selectedTaskForView.status
                        ) === 'finished' ? 'success' : 'default'
                      }
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t('tasks.startDate')}:</strong>{' '}
                    {selectedTaskForView.startDate ? formatDate(selectedTaskForView.startDate) : t('tasks.noStartDate')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{t('tasks.dueDate')}:</strong> {selectedTaskForView.dueDate ? formatDate(selectedTaskForView.dueDate) : t('tasks.noDueDate')}
                  </Typography>
                  {selectedTaskForView.description && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        <strong>{t('tasks.description')}:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedTaskForView.description}
                      </Typography>
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('tasks.assignees')}:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedTaskForView.assignees && selectedTaskForView.assignees.length > 0 ? (
                      selectedTaskForView.assignees.map((assignee: any) => (
                        <Chip
                          key={assignee.id}
                          label={`${assignee.firstName} ${assignee.lastName}`}
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('tasks.noAssignees')}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
              {selectedTaskForView.denialReason && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="warning">
                    <strong>{t('tasks.denialReason')}:</strong> {selectedTaskForView.denialReason}
                  </Alert>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTaskForView && (
            <>
              {getEffectiveTaskStatus(selectedTaskForView) === 'assigned' && isTaskAssignedToUser(selectedTaskForView) && (
                <Button
                  startIcon={<StartIcon />}
                  onClick={handleStartTask.bind(null, selectedTaskForView.id)}
                  variant="contained"
                  color="primary"
                >
                  {t('tasks.start')}
                </Button>
              )}
              {getEffectiveTaskStatus(selectedTaskForView) === 'started' && isTaskAssignedToUser(selectedTaskForView) && (
                <Button
                  startIcon={<CompleteIcon />}
                  onClick={handleCompleteTask.bind(null, selectedTaskForView.id)}
                  variant="contained"
                  color="success"
                >
                  {t('tasks.complete')}
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setViewTaskDialogOpen(false)}>{t('tasks.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Threshold Edit Dialog */}
      <Dialog open={thresholdDialogOpen} onClose={() => setThresholdDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks.editThreshold')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label={t('tasks.warningThreshold')}
            value={thresholdValue}
            onChange={(e) => setThresholdValue(e.target.value)}
            inputProps={{ min: 1 }}
            margin="normal"
            helperText={t('tasks.thresholdDescription', { count: parseInt(thresholdValue) || 0 })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThresholdDialogOpen(false)}>{t('tasks.cancel')}</Button>
          <Button 
            onClick={saveTaskWarningThreshold} 
            variant="contained"
            disabled={!thresholdValue || parseInt(thresholdValue, 10) < 1}
          >
            {t('tasks.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Menu */}
      <Menu
        anchorEl={calendarMenuAnchor}
        open={Boolean(calendarMenuAnchor)}
        onClose={handleCloseCalendarMenu}
      >
        <MuiMenuItem onClick={() => handleCalendarAction('outlook')}>
          {t('calendar.outlook')}
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleCalendarAction('google')}>
          {t('calendar.google')}
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleCalendarAction('ics')}>
          {t('calendar.downloadICS')}
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};

export default Projects; 